import { SupabaseClient } from '@supabase/supabase-js'
import { Database } from "@/supabase/types"
import { TablesInsert } from "@/supabase/types"
import { PermOptions } from '@/types/base/permissions'
import { Role } from "@/types/base/authuser"
import Authuser from '@/types/base/authuser'
import { createIsomorphicLogger } from '@/utils/logger/isomorphicLogger'
import { logError } from '@/utils/errorLogging'
import { printObject } from '@/lib/utils'
import { getTenantGroupsByTenantId } from './groups'
import { getTenantUsersByUserId } from './tenant-users'

const logger = createIsomorphicLogger('db.groupUsers')

export const getGroupUsersByGroupId = async (
	supabase: SupabaseClient<Database>,
  groupId: string
) => {
  // here we select with spread (...) to collapse the nesting
  const { data: groupUsers, error } = await supabase
    .from("groups")
    .select(
      `
        id,
        name,
        users:group_users(...tenant_users(tu_id:id, ...profiles(*)))
      `
    )
    .eq("id", groupId)
    .single()

  if (!groupUsers) {
    logError(logger, error, 'Failed to fetch Group Users', { groupId })
    throw new Error(error.message)
  }

  return groupUsers
}

export const isUserPartOfGroup = async (
	supabase: SupabaseClient<Database>,
  groupName: string,
	userId: string  //auth user id, like profile.user_id
): Promise<boolean> => {
	const tenantUser = await getTenantUsersByUserId(supabase, userId)
	if (!tenantUser) {
		return false
	}

	const tenantGroups = await getTenantGroupsByTenantId(supabase, tenantUser.tenant_id)
	const group = tenantGroups.find(tg => tg.name === groupName)
	if (!group) {
		return false
	}

	const { data: groupUser, error } = await supabase
		.from("group_users")
		.select("*")
    .eq("group_id", group.id)
    .eq("group_user_id", tenantUser.id)
		.maybeSingle()

	if (error) {
		logError(logger, error, 'Failed to fetch optional GroupUser', { groupName })
		throw new Error(error.message)
	}

	return groupUser !== null
}

export const createGroupUser = async (
	supabase: SupabaseClient<Database>,
  groupUser: TablesInsert<"group_users">
) => {
  const { data: createdGroupUser, error } = await supabase
    .from("group_users")
    .insert(groupUser)
    .select("*")

  if (!createdGroupUser) {
    logError(logger, error, 'Failed to create Group User', { groupUser: printObject(groupUser) })
    throw new Error(error.message)
  }

  logger.info('Group User created successfully', { groupUser: printObject(createdGroupUser) })
  return createdGroupUser
}

export const createGroupUsers = async (
	supabase: SupabaseClient<Database>,
  groupUsers: TablesInsert<"group_users">[]
) => {
  const { data: createdGroupUsers, error } = await supabase
    .from("group_users")
    .insert(groupUsers)
    .select("*")

  if (!createdGroupUsers) {
    logError(logger, error, 'Failed to create Group Users', { groupUsers: printObject(groupUsers) })
    throw new Error(error.message)
  }

  logger.info('Group Users created successfully', { groupUsers: printObject(createdGroupUsers) })
  return createdGroupUsers
}

export const deleteGroupUser = async (
	supabase: SupabaseClient<Database>,
  groupId: string,
  userId: string
) => {
  const { error } = await supabase
    .from("group_users")
    .delete()
    .eq("group_id", groupId)
    .eq("group_user_id", userId)

  if (error) {
    logError(logger, error, 'Failed to delete Group User', { groupId, userId })
    throw new Error(error.message)
  }

  return true
}

export const groupUsersCanPerform = async (supabase: SupabaseClient<Database>, authuser: Authuser, options: PermOptions) => {
	if (authuser.isAuthorizedForRole(Role.root)) {
		return true
	}

	switch (options.action) {
		default:
			return authuser.isAuthorizedForRole(Role.admin)
	}
}